/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import fileDownload from 'js-file-download';
import ImageViewerV2 from '../ImageViewerV2';
import {
  isImageAttachment as isImageItem,
  isPdfAttachment as isPdfItem,
  safeCreateObjectUrl,
} from '../filePreview/attachmentTypes';

/** Extensão (com ponto) → MIME(s) aceitos pelo react-dropzone */
const EXTENSION_MIME_MAP = {
  '.pdf': ['application/pdf'],
  '.png': ['image/png'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.bmp': ['image/bmp'],
  '.gif': ['image/gif'],
  '.webp': ['image/webp'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.xls': ['application/vnd.ms-excel'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  '.xlsb': ['application/vnd.ms-excel.sheet.binary.macroenabled.12'],
  '.kmz': ['application/vnd.google-earth.kmz', 'application/zip', 'application/x-zip-compressed'],
  '.kml': ['application/vnd.google-earth.kml+xml', 'application/xml', 'text/xml'],
  '.json': ['application/json'],
  '.csv': ['text/csv'],
};

const buildDropzoneAccept = (acceptedFileTypes) => {
  if (!acceptedFileTypes?.length) return undefined;

  return acceptedFileTypes.reduce((acc, raw) => {
    const token = String(raw).trim();
    const lower = token.toLowerCase();

    if (!lower.startsWith('.')) {
      if (!acc[token]) acc[token] = [];
      return acc;
    }

    const mimes = EXTENSION_MIME_MAP[lower];
    if (!mimes?.length) return acc;

    mimes.forEach((mime) => {
      if (!acc[mime]) acc[mime] = [];
      if (!acc[mime].includes(lower)) acc[mime].push(lower);
    });
    return acc;
  }, {});
};

const canPreviewItem = (fileItem) =>
  !!fileItem.file && (isImageItem(fileItem) || isPdfItem(fileItem));

// ---------------------------------------------------------------------------
// Dialog de upload temático V2
// ---------------------------------------------------------------------------

const DropzoneDialogV2 = ({
  open,
  onClose,
  onSave,
  acceptedFiles: acceptedFileTypes,
  maxFileSize,
  filesLimit,
  dialogTitle = 'Selecionar arquivos',
}) => {
  const theme = useTheme();
  const { neutral, primary, mode } = theme.palette;

  const [stagedFiles, setStagedFiles] = useState([]);
  const [rejectMessage, setRejectMessage] = useState('');

  const headerBg =
    mode === 'dark'
      ? `linear-gradient(145deg, ${neutral[900]} 0%, ${neutral[800]} 55%, ${alpha(neutral[700], 0.92)} 100%)`
      : `linear-gradient(145deg, ${neutral[800]} 0%, ${neutral[700]} 45%, ${alpha(primary.main, 0.12)} 100%)`;
  const headerBorder = `1px solid ${alpha(mode === 'dark' ? neutral[50] : neutral[900], 0.08)}`;
  const contentBg = mode === 'dark' ? alpha(neutral[900], 0.45) : neutral[100];
  const actionsBg = mode === 'dark' ? alpha(neutral[800], 0.85) : neutral[50];

  const onDrop = useCallback(
    (accepted, rejected) => {
      setRejectMessage('');
      if (rejected && rejected.length > 0) {
        const reasons = rejected.map((r) => {
          const err = r.errors?.[0];
          if (err?.code === 'file-too-large') return `"${r.file.name}" é muito grande`;
          if (err?.code === 'file-invalid-type') return `"${r.file.name}" não é um tipo suportado`;
          return `"${r.file.name}" foi rejeitado`;
        });
        setRejectMessage(reasons.join('; '));
        return;
      }
      setStagedFiles((prev) => {
        const merged = [...prev, ...accepted];
        return filesLimit ? merged.slice(0, filesLimit) : merged;
      });
    },
    [filesLimit],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    multiple: filesLimit !== 1,
    accept: buildDropzoneAccept(acceptedFileTypes),
  });

  const handleClose = () => {
    setStagedFiles([]);
    setRejectMessage('');
    onClose();
  };

  const handleSave = () => {
    onSave(stagedFiles);
    setStagedFiles([]);
    setRejectMessage('');
  };

  const removeStaged = (index) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          background: headerBg,
          borderBottom: headerBorder,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2 },
          position: 'relative',
          '&::after': {
            background: `linear-gradient(90deg, ${primary.main} 0%, ${alpha(primary.main, 0.35)} 55%, transparent 100%)`,
            bottom: 0,
            content: '""',
            height: 3,
            left: 0,
            opacity: 0.9,
            position: 'absolute',
            width: '38%',
          },
        }}
      >
        <Typography
          component="span"
          variant="h6"
          sx={{
            color: 'common.white',
            fontWeight: 600,
            letterSpacing: 0.3,
            textShadow: `0 1px 2px ${alpha('#000', 0.25)}`,
          }}
        >
          {dialogTitle}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: contentBg, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          <Box
            {...getRootProps()}
            sx={{
              border: `2px dashed ${isDragActive ? primary.main : alpha(mode === 'dark' ? neutral[400] : neutral[500], 0.45)}`,
              borderRadius: 2,
              backgroundColor: isDragActive
                ? alpha(primary.main, 0.08)
                : mode === 'dark'
                  ? alpha(neutral[800], 0.5)
                  : alpha(neutral[200], 0.5),
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              minHeight: 140,
              px: 3,
              py: 4,
              transition: theme.transitions.create(['border-color', 'background-color'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                borderColor: primary.main,
                backgroundColor: alpha(primary.main, 0.06),
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadFileOutlinedIcon
              sx={{ fontSize: 40, color: isDragActive ? primary.main : 'text.secondary', mb: 0.5 }}
            />
            <Typography
              variant="body1"
              sx={{ color: isDragActive ? primary.main : 'text.primary', fontWeight: 500, textAlign: 'center' }}
            >
              {isDragActive ? 'Solte o arquivo aqui' : 'Arraste e solte um arquivo aqui ou clique'}
            </Typography>
            {acceptedFileTypes && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {acceptedFileTypes.join(', ')}
              </Typography>
            )}
          </Box>

          {stagedFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                Pré-visualização:
              </Typography>
              <List dense disablePadding>
                {stagedFiles.map((f, i) => (
                  <ListItem
                    key={i}
                    disablePadding
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => removeStaged(i)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{
                      borderRadius: 1,
                      px: 1,
                      mb: 0.5,
                      backgroundColor: mode === 'dark' ? alpha(neutral[700], 0.4) : alpha(neutral[200], 0.6),
                    }}
                  >
                    <InsertDriveFileOutlinedIcon fontSize="small" sx={{ mr: 1, color: primary.main }} />
                    <ListItemText
                      primary={f.name}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      secondary={`${(f.size / 1024).toFixed(1)} KB`}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {rejectMessage && (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {rejectMessage}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: actionsBg,
          borderTop: `1px solid ${alpha(mode === 'dark' ? neutral[100] : neutral[900], 0.08)}`,
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderColor: alpha(neutral[400], mode === 'dark' ? 0.45 : 0.55),
            color: 'text.primary',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: alpha(neutral[500], 0.1),
              borderColor: neutral[400],
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={stagedFiles.length === 0}
          onClick={handleSave}
          sx={{
            fontWeight: 700,
            minWidth: 100,
            px: 2.5,
            boxShadow: (t) => `0 4px 16px ${alpha(t.palette.primary.main, 0.42)}`,
            '&:hover:not(:disabled)': {
              boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.5)}`,
              transform: 'translateY(-1px)',
            },
            transition: (t) =>
              t.transitions.create(['box-shadow', 'transform'], {
                duration: t.transitions.duration.short,
              }),
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DropzoneDialogV2.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  acceptedFiles: PropTypes.array,
  maxFileSize: PropTypes.number,
  filesLimit: PropTypes.number,
  dialogTitle: PropTypes.string,
};

// ---------------------------------------------------------------------------
// DropzoneV2
// ---------------------------------------------------------------------------

const DropzoneV2 = ({
  onSave,
  acceptedFiles,
  showPreviews,
  maxFileSize,
  buttonText,
  errorMessage,
  filesLimit,
  disabled,
  showGridFiles,
  files,
  canDeleteFiles,
  renderFotos,
  error,
  buttonClassName,
  showImageViewer,
  buttonColor,
  leadingAction,
}) => {
  const [open, setOpen] = useState(false);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);

  const onSaveLocal = (nextFiles) => {
    setOpen(false);
    onSave(nextFiles);
  };

  const previewFiles = (files || []).filter((f) => isImageItem(f) || isPdfItem(f));

  const handlePreview = (fileItem) => {
    if (!fileItem.file) return;
    const viewerIndex = previewFiles.findIndex((f) => f === fileItem);
    if (viewerIndex < 0) return;
    setImageViewerIndex(viewerIndex);
    setOpenImageViewer(true);
  };

  const imageViewerImages = previewFiles.map((f) => ({
    src: isImageItem(f) ? (f.imageSrc || safeCreateObjectUrl(f.file)) : '',
    nome: f.nome || '',
    extensao: isPdfItem(f) ? (f.extensao || 'pdf') : f.extensao,
    file: f.file,
    additions: f.additions,
  }));

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {leadingAction}
          <Button
            color={buttonColor}
            variant="contained"
            onClick={() => setOpen(true)}
            disabled={disabled}
            className={buttonClassName}
          >
            {buttonText}
          </Button>
        </Box>
      </Grid>

      {showGridFiles && (
        <Grid size={{ xs: 12 }}>
          {files &&
            files.map((file, index) => (
              <React.Fragment key={index}>
                {renderFotos ? (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                      <Grid container direction="row" justifyContent="center" alignItems="center">
                        <Grid size={{ xs: 12 }}>
                          <Grid container direction="row" justifyContent="center" alignItems="center">
                            <img
                              alt=""
                              src={file.imageSrc}
                              style={{ height: 50, cursor: 'pointer' }}
                              onClick={() => {
                                if (showImageViewer) {
                                  handlePreview(file);
                                } else {
                                  file.onClick(file);
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Grid container direction="row" justifyContent="center" alignItems="center">
                            <p>
                              {file.nome}
                              {canDeleteFiles && (
                                <IconButton onClick={() => file.onDelete(file)}>
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </p>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}>
                      <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: 'text.primary', mr: 0.25 }}>
                        {file.nome}
                      </Typography>

                      {canPreviewItem(file) && (
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => handlePreview(file)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {file.file && (
                        <Tooltip title="Baixar">
                          <IconButton size="small" onClick={() => fileDownload(file.file, file.nome)}>
                            <DownloadOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {!disabled && canDeleteFiles && (
                        <Tooltip title="Remover">
                          <IconButton size="small" onClick={() => file.onDelete(file)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                )}
              </React.Fragment>
            ))}

          {error && (
            <Grid size={{ xs: 12 }}>
              <Box
                component="p"
                sx={{
                  color: 'error.main',
                  fontSize: 11,
                  mt: 1,
                  mx: 1.5,
                  minHeight: '1em',
                  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1,
                  letterSpacing: 0.33,
                }}
              >
                {errorMessage ? errorMessage : `${buttonText} é obrigatório`}
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <DropzoneDialogV2
        open={open}
        onSave={onSaveLocal}
        acceptedFiles={acceptedFiles}
        showPreviews={showPreviews}
        maxFileSize={maxFileSize}
        onClose={() => setOpen(false)}
        filesLimit={filesLimit}
      />

      <ImageViewerV2
        open={openImageViewer}
        onClose={() => setOpenImageViewer(false)}
        images={imageViewerImages}
        activeIndex={imageViewerIndex}
        showInfoPanel
      />
    </>
  );
};

DropzoneV2.propTypes = {
  acceptedFiles: PropTypes.array,
  showPreviews: PropTypes.bool,
  maxFileSize: PropTypes.number,
  buttonText: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  filesLimit: PropTypes.number,
  disabled: PropTypes.bool,
  showGridFiles: PropTypes.bool,
  files: PropTypes.array,
  canDeleteFiles: PropTypes.bool,
  renderFotos: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  showImageViewer: PropTypes.bool,
  buttonClassName: PropTypes.string,
  buttonColor: PropTypes.string,
  leadingAction: PropTypes.node,
};

DropzoneV2.defaultProps = {
  acceptedFiles: ['image/jpeg', 'image/png', 'image/bmp'],
  showPreviews: true,
  maxFileSize: 5000000,
  buttonText: 'Adicionar Imagens',
  filesLimit: 10,
  disabled: false,
  showGridFiles: false,
  files: [],
  canDeleteFiles: false,
  renderFotos: false,
  error: false,
  errorMessage: null,
  showImageViewer: false,
  buttonClassName: undefined,
  buttonColor: 'primary',
};

export default DropzoneV2;
export { DropzoneDialogV2 };
